import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { PACKAGE_NAME } from '@atlassian/jira-servicedesk-getting-started-panel-common/src/constants.tsx';
import { withExponentialBackoffRetries } from '@atlassian/jira-servicedesk-getting-started-panel-common/src/utils/with-exponential-backoff-retries/index.tsx';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { ActiveState, PropertyKey, VisualState } from '@atlassian/jsm-getting-started-panel';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { State, ActionProps } from '../../types.tsx';
import { initializeState } from '../initialize-state/index.tsx';
import { saveProperty } from '../save-property/index.tsx';

export type ReopenGettingStartedPanel = (actionProps?: { projectId?: ProjectId }) => Promise<void>;

export const reopenGettingStartedPanel =
	(actionProps: ActionProps) =>
	({ getState, dispatch }: StoreActionApi<State>): ReopenGettingStartedPanel =>
	async ({ projectId } = {}) => {
		const getStateIfLoaded = async () => {
			const state = getState();
			const dataNotYetLoaded = state.isGspInitialized && state.data === undefined;
			if (dataNotYetLoaded) {
				throw new Error('GSP data not yet loaded when trying to reopen');
			}
			return state;
		};

		try {
			// a user can exit a tour and call `reopenGettingStartedPanel` before the GSP
			// state has loaded so retry a few times and hopefully the data will be there.
			// This will only log the final error that is thrown after the retries are exhausted.
			// 6 attempts at 500ms base interval gives us a ~15 second window. We could wait longer
			// but then the GSP reopening would be so far removed from closing the tour that it
			// probably isn't the user experience that we want
			const { data, isGspInitialized } = await withExponentialBackoffRetries({
				callback: getStateIfLoaded,
				totalAttempts: 6,
				baseIntervalMs: 500,
			});

			if (!isGspInitialized) {
				if (projectId !== undefined) {
					await dispatch(initializeState(actionProps))({ projectId });
					return;
				}
				throw new Error('No projectId to initialize GSP with');
			}

			if (!data) {
				throw new Error('Trying to reopen GSP without current state');
			}

			await dispatch(saveProperty(actionProps))({
				key: PropertyKey.VisualState,
				value: VisualState.FullPanel,
			});
			await dispatch(saveProperty(actionProps))({
				key: PropertyKey.ActiveState,
				value: ActiveState.On,
			});
			fireTrackAnalytics(
				actionProps.createAnalyticsEvent({}),
				`${PACKAGE_NAME}.reopenGettingStartedPanel succeeded`,
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'reopenGettingStartedPanel',
					packageName: PACKAGE_NAME,
					teamName: 'itops-mithril',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			throw error;
		}
	};
